/**
 * Notification Preferences Service
 *
 * Handles user notification preferences including:
 * - Email notification settings
 * - Push notification settings
 * - Alert channel configuration
 * - Quiet hours settings
 *
 * Syncs preferences with backend API and uses localStorage as fallback.
 * DEV-1705: Notification Preferences Backend Sync
 *
 * GAP-007 (2026-06-03): removed the `as unknown as` casts that pretended the
 * generated `NotificationPreferences` (categories-based / per-channel `channels[]`
 * shape) was structurally identical to the legacy UI shape
 * (`alertChannels`-by-severity, `tradingAlerts`/`systemAlerts`/… booleans).
 * At runtime those casts produced an object whose UI fields were ALL `undefined`,
 * so the component silently fell back to hardcoded defaults while still flagging
 * the preferences as "Synced" — and a subsequent save wrote those fabricated
 * defaults back over the user's real settings.
 *
 * This file now contains a genuine bidirectional adapter between the generated
 * API contract and the UI-facing model. The adapter:
 *   - reads every real backend field into the UI shape (email categories +
 *     digest, push.enabled, quiet hours, per-channel `channels[]` → severity grid);
 *   - on update, rebuilds the full API request from the UI state MERGED onto the
 *     original backend object, so fields the UI cannot represent (digest enum
 *     nuance like "never", SMS phone, IANA timezone, channel ids/destinations,
 *     non-grid channel types such as slack/teams/webhook) are preserved rather
 *     than destroyed.
 */
import { Injectable, inject, signal, computed, Injector, DestroyRef } from '@angular/core';
import { toObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SharedAuthStateService } from '@stores/shared-auth-state.service';
import { TenantStore } from '@stores/tenant.store';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, Subject, EMPTY, merge, defer } from 'rxjs';
import { catchError, map, tap, filter, switchMap, concatMap, distinctUntilChanged, timeout } from 'rxjs/operators';
import { UserApiService } from '@api/api/user.service';
import { AlertChannelType } from '@api/model/alertChannelType';
import type { NotificationPresentationPreferences } from '@api/model/notificationPresentationPreferences';
import type { NotificationPreferences as ApiNotificationPreferences } from '@api/model/notificationPreferences';
import type { NotificationPreferencesResponse as ApiNotificationPreferencesResponse } from '@api/model/notificationPreferencesResponse';
import type { UpdateNotificationPreferencesRequest as ApiUpdateNotificationPreferencesRequest } from '@api/model/updateNotificationPreferencesRequest';
import type { EmailPreferences as ApiEmailPreferences } from '@api/model/emailPreferences';
import type { PushPreferences as ApiPushPreferences } from '@api/model/pushPreferences';
import type { QuietHours as ApiQuietHours } from '@api/model/quietHours';
import type { AlertChannelConfig as ApiAlertChannelConfig } from '@api/model/alertChannelConfig';
import { LoggerService } from '@core/services/logger.service';

/**
 * Email notification settings structure (UI-facing).
 *
 * Maps to the generated `EmailPreferences` as follows:
 *   tradingAlerts    ↔ categories.trading
 *   systemAlerts     ↔ categories.account
 *   securityAlerts   ↔ categories.security
 *   marketingUpdates ↔ categories.marketing
 *   weeklyDigest     ↔ (digest === 'weekly')
 *   frequency        ↔ digest ('instant'→'immediate', 'daily'/'weekly'→'daily',
 *                              'never'→'immediate'); 'hourly' has no backend
 *                              digest value and persists as 'daily'.
 *
 * The UI cannot represent the backend 'never' digest, so on save the baseline
 * digest is preserved when the digest-relevant controls (weeklyDigest +
 * frequency) are unchanged — see `resolveDigest`. This prevents an unrelated
 * save from silently turning 'never' into 'instant'.
 */
export interface EmailNotificationSettings {
  tradingAlerts: boolean;
  systemAlerts: boolean;
  securityAlerts: boolean;
  weeklyDigest: boolean;
  marketingUpdates: boolean;
  frequency: 'immediate' | 'hourly' | 'daily';
}

/**
 * Push notification settings structure (UI-facing).
 *
 * `browserPush` maps to the generated `PushPreferences.enabled`. `mobilePush`
 * and `desktopPermissionGranted` are client-only state with no backend field;
 * the component sources them from the browser Notification API / localStorage.
 */
export interface PushNotificationSettings {
  browserPush: boolean;
  mobilePush: boolean;
  desktopPermissionGranted: boolean;
}

/**
 * Alert channel configuration per channel type.
 *
 * Only the channel types the backend can persist are modelled. Backend
 * `AlertChannelType` has no `push` kind, so there is intentionally no `push`
 * column here — exposing an editable Push cell would silently discard the
 * operator's toggle on save (the GAP-007 false-assurance pattern).
 */
export interface AlertChannelConfig {
  email: boolean;
  sms: boolean;
}

/**
 * Alert channel settings by severity level.
 *
 * Derived from the generated `channels: AlertChannelConfig[]` — a cell is `true`
 * when an enabled channel of that `channelType` covers the severity (an empty
 * `severityFilter` means "all severities").
 */
export interface AlertChannelSettings {
  critical: AlertChannelConfig;
  warning: AlertChannelConfig;
  info: AlertChannelConfig;
}

/**
 * Quiet hours settings structure (UI-facing).
 *
 * Maps to the generated `QuietHours`: startTime ↔ start, endTime ↔ end. The
 * backend `timezone` field has no UI control and is preserved across saves.
 */
export interface QuietHoursSettings {
  enabled: boolean;
  startTime: string;
  endTime: string;
}

/**
 * Complete notification preferences structure (UI-facing).
 */
export interface NotificationPreferences {
  email: EmailNotificationSettings;
  push: PushNotificationSettings;
  alertChannels: AlertChannelSettings;
  quietHours: QuietHoursSettings;
}

/**
 * Response from GET /api/v1/users/notification-preferences.
 *
 * `preferences` is the adapted UI shape. `apiPreferences` is the untouched
 * backend object — the component passes it back on save so unmapped fields are
 * preserved rather than overwritten with defaults.
 */
export interface NotificationPreferencesResponse {
  preferences: NotificationPreferences;
  apiPreferences: ApiNotificationPreferences;
  updatedAt: string | null;
}

/**
 * Request for PUT /api/v1/users/notification-preferences.
 *
 * `preferences` carries the user's full desired UI state. `baseline` is the
 * last-known backend object used to preserve fields the UI does not model.
 */
export interface UpdateNotificationPreferencesRequest {
  preferences: NotificationPreferences;
  baseline: ApiNotificationPreferences;
}

export const DEFAULT_NOTIFICATION_PRESENTATION: NotificationPresentationPreferences = {
  enableSound: true,
  enableDesktop: true,
  enableToast: true,
  soundVolume: 0.5
};

const UI_SEVERITIES = ['critical', 'warning', 'info'] as const;
type UiSeverity = (typeof UI_SEVERITIES)[number];

/**
 * Backend severity tokens (see VALID_SEVERITIES in
 * src/api_server/routes/notification_preferences.rs) that the UI grid does not
 * render. Coverage for these MUST survive a UI round trip so saving never
 * silently disables, for example, an operator's `error` alerts.
 */
const BACKEND_NON_UI_SEVERITIES = ['error'] as const;

@Injectable({
  providedIn: 'root'
})
export class NotificationPreferencesService {
  private readonly api = inject(UserApiService);
  private readonly logger = inject(LoggerService);
  private readonly auth = inject(SharedAuthStateService);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  // Resolve TenantStore lazily: it consumes NotificationService during bootstrap.
  private readonly scope = computed(() => {
    if (!this.auth.isAuthenticated() || !this.auth.currentUserId()) return null;
    const tenant = this.injector.get(TenantStore).currentTenant()?.id ?? this.auth.currentTenantId();
    return tenant ? `${this.auth.currentUserId()}:${tenant}` : null;
  });
  private readonly presentationState = signal<{
    scope: string;
    value: NotificationPresentationPreferences;
    status: 'loading' | 'synced' | 'saving' | 'error';
    loaded: boolean;
  } | null>(null);
  private revision = 0;
  private readonly presentationWrites = new Subject<{
    scope: string; revision: number; value: NotificationPresentationPreferences;
  }>();
  readonly presentation = computed(() => {
    const state = this.presentationState();
    return state?.scope === this.scope() ? state.value : DEFAULT_NOTIFICATION_PRESENTATION;
  });
  readonly presentationStatus = computed(() => {
    const state = this.presentationState();
    return state?.scope === this.scope() ? state.status : 'loading';
  });
  readonly presentationReady = computed(() => {
    const state = this.presentationState();
    return !!this.scope() && state?.scope === this.scope() && state.loaded;
  });
  private readonly reloadPresentation = new Subject<void>();

  constructor() {
    toObservable(this.scope).pipe(
      map(() => this.scope()),
      distinctUntilChanged(),
      switchMap(scope => {
        this.revision++;
        this.presentationState.set(scope ? {
          scope, value: { ...DEFAULT_NOTIFICATION_PRESENTATION }, status: 'loading', loaded: false
        } : null);
        if (!scope) return EMPTY;
        const load = () => this.getPreferences().pipe(
          timeout(15_000),
          catchError(() => {
            if (this.scope() === scope) this.presentationState.update(state =>
              state ? { ...state, status: 'error' } : state);
            return EMPTY;
          })
        );
        return merge(
          defer(load),
          this.reloadPresentation.pipe(switchMap(load)),
          this.presentationWrites.pipe(concatMap(write => {
            if (write.scope !== this.scope()) return EMPTY;
            return this.api.updateNotificationPreferences0({ presentation: write.value }, 'body').pipe(
              timeout(15_000),
              tap(response => {
                const saved = response.preferences.presentation;
                if (!saved || saved.enableSound !== write.value.enableSound ||
                    saved.enableDesktop !== write.value.enableDesktop ||
                    saved.enableToast !== write.value.enableToast ||
                    saved.soundVolume !== write.value.soundVolume) {
                  throw new Error('The server did not confirm notification presentation preferences.');
                }
                if (this.scope() === write.scope && this.revision === write.revision) {
                  this.publishPresentation(response.preferences, write.scope, 'synced');
                }
              }),
              catchError(() => {
                if (this.scope() === write.scope && this.revision === write.revision) {
                  this.presentationState.update(state => state ? { ...state, status: 'error' } : state);
                }
                return EMPTY;
              })
            );
          }))
        );
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  retryPresentation(): void {
    this.reloadPresentation.next();
  }

  updatePresentation(patch: Partial<NotificationPresentationPreferences>): void {
    const scope = this.scope();
    if (!scope || !this.presentationReady()) return;
    const value = { ...this.presentation(), ...patch };
    if (!Number.isFinite(value.soundVolume) || value.soundVolume < 0 || value.soundVolume > 1) return;
    const revision = ++this.revision;
    this.presentationState.set({ scope, value, status: 'saving', loaded: true });
    this.presentationWrites.next({ scope, revision, value });
  }

  private publishPresentation(
    preferences: ApiNotificationPreferences,
    scope: string,
    status: 'synced'
  ): void {
    this.presentationState.set({
      scope, value: { ...(preferences.presentation ?? DEFAULT_NOTIFICATION_PRESENTATION) },
      status, loaded: true
    });
  }


  /**
   * Get user's notification preferences from backend and adapt them to the UI
   * model. Every UI field is read from a concrete backend field — no field is
   * left `undefined` to silently fall back to a hardcoded default.
   */
  getPreferences(): Observable<NotificationPreferencesResponse> {
    // Pass explicit `observe: 'body'` so TypeScript picks the
    // Observable<NotificationPreferencesResponse> overload.
    return defer(() => {
      const scope = this.scope();
      const revision = this.revision;
      const startedWhileSaving = this.presentationStatus() === 'saving';
      return this.api.getNotificationPreferences0('body').pipe(
      filter(() => this.scope() === scope),
      tap(response => {
        if (scope && !startedWhileSaving && this.revision === revision && this.presentationStatus() !== 'saving') this.publishPresentation(response.preferences, scope, 'synced');
      }),
      map((response: ApiNotificationPreferencesResponse): NotificationPreferencesResponse => ({
        preferences: this.toUiPreferences(response.preferences),
        apiPreferences: response.preferences,
        updatedAt: response.updatedAt ?? null
      })),
      tap(() => this.logDebug('getPreferences', 'Fetched and adapted notification preferences from backend')),
      catchError(this.handleError('getPreferences'))
      );
    });
  }

  /**
   * Update user's notification preferences on backend.
   *
   * Builds the full API request by merging the UI state onto the supplied
   * backend baseline so unmodelled fields survive the round trip.
   */
  updatePreferences(request: UpdateNotificationPreferencesRequest): Observable<NotificationPreferencesResponse> {
    const apiRequest = this.toApiUpdateRequest(request.preferences, request.baseline);
    return defer(() => {
      const scope = this.scope();
      return this.api.updateNotificationPreferences0(apiRequest, 'body').pipe(
      filter(() => this.scope() === scope),
      map((response: ApiNotificationPreferencesResponse): NotificationPreferencesResponse => ({
        preferences: this.toUiPreferences(response.preferences),
        apiPreferences: response.preferences,
        updatedAt: response.updatedAt ?? null
      })),
      tap(() => this.logDebug('updatePreferences', 'Updated notification preferences on backend')),
      catchError(this.handleError('updatePreferences'))
      );
    });
  }

  /** Add an explicitly entered email destination, disabled until the user enables alerts. */
  addEmailDestination(destination: string): Observable<NotificationPreferencesResponse> {
    const userId = this.auth.currentUserId();
    if (!this.auth.isAuthenticated() || !userId) {return throwError(() => new Error("Sign in before adding a destination."));}
    const tenantId = this.injector.get(TenantStore).currentTenant()?.id ?? this.auth.currentTenantId();
    return this.api.getNotificationPreferences0('body').pipe(
      switchMap(response => {
        if (!this.auth.isAuthenticated() || userId !== this.auth.currentUserId() ||
            tenantId !== (this.injector.get(TenantStore).currentTenant()?.id ?? this.auth.currentTenantId())) {
          return throwError(() => new Error('Account context changed. Reopen notification settings.'));
        }
        const channels = response.preferences.channels ?? [];
        const email = destination.trim();
        if (channels.some(channel => channel.channelType === AlertChannelType.Email &&
            channel.destination.toLowerCase() === email.toLowerCase())) {
          return throwError(() => new Error('This email destination is already configured.'));
        }
        return this.api.updateNotificationPreferences0({ channels: [...channels, {
          channelType: AlertChannelType.Email, destination: email, enabled: false, severityFilter: [],
        }] }, 'body');
      }),
      map(response => {
        if (!this.auth.isAuthenticated() || userId !== this.auth.currentUserId() ||
            tenantId !== (this.injector.get(TenantStore).currentTenant()?.id ?? this.auth.currentTenantId())) {
          throw new Error('Account context changed. Reopen notification settings.');
        }
        return {
          preferences: this.toUiPreferences(response.preferences),
          apiPreferences: response.preferences,
          updatedAt: response.updatedAt ?? null,
        };
      }),
      catchError(this.handleError('addEmailDestination')),
    );
  }

  // ==========================================================================
  // Adapter: backend contract → UI model
  // ==========================================================================

  /**
   * Adapt the generated `NotificationPreferences` into the UI-facing shape.
   */
  private toUiPreferences(api: ApiNotificationPreferences): NotificationPreferences {
    return {
      email: this.toUiEmail(api.email),
      push: this.toUiPush(api.push),
      alertChannels: this.toUiAlertChannels(api.channels ?? []),
      quietHours: this.toUiQuietHours(api.quietHours)
    };
  }

  private toUiEmail(email: ApiEmailPreferences): EmailNotificationSettings {
    return {
      tradingAlerts: email.categories.trading,
      systemAlerts: email.categories.account,
      securityAlerts: email.categories.security,
      marketingUpdates: email.categories.marketing,
      weeklyDigest: email.digest === 'weekly',
      frequency: this.digestToFrequency(email.digest)
    };
  }

  private toUiPush(push: ApiPushPreferences): PushNotificationSettings {
    return {
      browserPush: push.enabled,
      // No backend field for mobile/desktop-permission; sourced client-side.
      mobilePush: false,
      desktopPermissionGranted: false
    };
  }

  private toUiQuietHours(quietHours: ApiQuietHours): QuietHoursSettings {
    return {
      enabled: quietHours.enabled,
      startTime: quietHours.start,
      endTime: quietHours.end
    };
  }

  /**
   * Collapse the rich per-channel `channels[]` list into the severity ×
   * {email,sms} boolean grid. A cell is enabled when at least one enabled
   * channel of that type covers the severity. Backend has no `push` channel
   * type, so push is intentionally not part of this grid.
   */
  private toUiAlertChannels(channels: ApiAlertChannelConfig[]): AlertChannelSettings {
    const cell = (severity: UiSeverity): AlertChannelConfig => ({
      email: this.channelCovers(channels, AlertChannelType.Email, severity),
      sms: this.channelCovers(channels, AlertChannelType.Sms, severity)
    });
    return {
      critical: cell('critical'),
      warning: cell('warning'),
      info: cell('info')
    };
  }

  private channelCovers(
    channels: ApiAlertChannelConfig[],
    type: AlertChannelType,
    severity: UiSeverity
  ): boolean {
    return channels.some(ch =>
      ch.enabled &&
      ch.channelType === type &&
      (ch.severityFilter.length === 0 ||
        ch.severityFilter.some(s => s.toLowerCase() === severity))
    );
  }

  private digestToFrequency(digest: string): EmailNotificationSettings['frequency'] {
    switch (digest) {
      case 'daily':
      case 'weekly':
        return 'daily';
      case 'instant':
      case 'never':
      default:
        return 'immediate';
    }
  }

  // ==========================================================================
  // Adapter: UI model → backend update request
  // ==========================================================================

  /**
   * Build the API update request by merging the UI state onto the backend
   * baseline. Fields the UI does not model (digest "never"/"weekly" nuance,
   * SMS preferences, quiet-hours timezone, channel ids/destinations/quietHours
   * and non-grid channel types) are carried over from the baseline so they are
   * not silently destroyed.
   */
  private toApiUpdateRequest(
    ui: NotificationPreferences,
    baseline: ApiNotificationPreferences
  ): ApiUpdateNotificationPreferencesRequest {
    return {
      email: this.toApiEmail(ui.email, baseline.email),
      sms: baseline.sms,
      push: this.toApiPush(ui.push, baseline.push),
      quietHours: this.toApiQuietHours(ui.quietHours, baseline.quietHours),
      channels: this.toApiChannels(ui.alertChannels, baseline.channels ?? [])
    };
  }

  private toApiEmail(
    ui: EmailNotificationSettings,
    baseline: ApiEmailPreferences
  ): ApiEmailPreferences {
    return {
      enabled: baseline.enabled,
      digest: this.resolveDigest(ui, baseline.digest),
      categories: {
        security: ui.securityAlerts,
        trading: ui.tradingAlerts,
        account: ui.systemAlerts,
        marketing: ui.marketingUpdates
      }
    };
  }

  private toApiPush(
    ui: PushNotificationSettings,
    baseline: ApiPushPreferences
  ): ApiPushPreferences {
    return {
      enabled: ui.browserPush,
      // Push categories have no UI control; preserve the backend value.
      categories: baseline.categories
    };
  }

  private toApiQuietHours(
    ui: QuietHoursSettings,
    baseline: ApiQuietHours
  ): ApiQuietHours {
    return {
      enabled: ui.enabled,
      start: ui.startTime,
      end: ui.endTime,
      // Timezone has no UI control; preserve the backend value.
      timezone: baseline.timezone
    };
  }

  /**
   * Rebuild the `channels[]` list from the UI grid while preserving everything
   * the grid cannot express:
   *   - existing email/sms channels keep their id, destination and quietHours;
   *     only their `enabled` flag and severity coverage are reconciled;
   *   - non-grid channel types (slack/teams/webhook) are carried over verbatim;
   *   - severity tokens the UI grid does not render — the backend `error`
   *     severity and any future tokens — are preserved, and the empty-filter
   *     "all severities" case is kept as `[]` rather than narrowed to the three
   *     enumerated UI severities.
   *
   * The grid models one logical email channel and one logical sms channel,
   * each exposing the critical/warning/info cells for that type. A save is
   * compared with aggregate coverage for that type:
   *   - if the operator left the grid cells exactly as loaded, the baseline
   *     channel (filter + enabled) is returned verbatim — a lossless round trip
   *     for `[]`/all-severities, `error`-only and mixed channels alike;
   *   - if a cell changed, the new filter merges the ticked UI severities with
   *     the baseline's non-grid tokens (`error`, future tokens). When the
   *     baseline covered all severities (`[]`) and a UI cell was unticked, the
   *     "all" shorthand can no longer apply, so coverage is enumerated as the
   *     ticked UI severities plus every backend non-UI severity (preserving
   *     `error` coverage the operator never touched).
   *
   * When a type has no existing channel, no channel is invented (a real backend
   * channel requires a `destination`, which the grid does not provide). To
   * prevent a silent drop under a success banner, the component disables the
   * grid cells for any channel type that has no configured destination, so the
   * operator can never tick a cell whose value this method would discard.
   */
  private toApiChannels(
    ui: AlertChannelSettings,
    baseline: ApiAlertChannelConfig[]
  ): ApiAlertChannelConfig[] {
    const gridTypes = new Set<AlertChannelType>([AlertChannelType.Email, AlertChannelType.Sms]);

    const baselineGrid = this.toUiAlertChannels(baseline);
    return baseline.map(ch => {
      if (!gridTypes.has(ch.channelType)) {
        // slack / teams / webhook — untouched by the grid.
        return ch;
      }

      const selected = this.selectedUiSeverities(ui, ch.channelType);
      const baselineSelected = this.selectedUiSeverities(baselineGrid, ch.channelType);
      // The UI represents aggregate coverage for the channel type. An unrelated
      // save must preserve each destination, including newly added disabled ones.
      if (selected.length === baselineSelected.length && baselineSelected.every(severity => selected.includes(severity))) {
        return ch;
      }

      // An empty filter on a disabled destination never provided hidden 'error'
      // coverage. Its first opt-in should include only explicitly selected cells.
      const merged = !ch.enabled && ch.severityFilter.length === 0
        ? [...selected]
        : this.mergeSeverityFilter(selected, ch.severityFilter);
      return {
        ...ch,
        enabled: selected.length > 0,
        severityFilter: merged
      };
    });
  }

  /** UI severities (critical/warning/info) ticked in the grid for a channel type. */
  private selectedUiSeverities(ui: AlertChannelSettings, type: AlertChannelType): UiSeverity[] {
    const key: keyof AlertChannelConfig = type === AlertChannelType.Email ? 'email' : 'sms';
    return UI_SEVERITIES.filter(sev => ui[sev][key]);
  }

  /**
   * Combine the grid's ticked UI severities with the severity tokens the grid
   * cannot represent, so saving never drops `error`/future coverage:
   *   - non-grid tokens already on the baseline filter (e.g. `error`) are kept;
   *   - if the baseline filter was empty ("all severities") it implicitly
   *     covered the backend non-UI severities too, so those are added back to
   *     avoid silently dropping them when the "all" shorthand can no longer be
   *     used (because a UI cell was unticked).
   */
  private mergeSeverityFilter(selected: UiSeverity[], baselineFilter: string[]): string[] {
    const uiSet = new Set<string>(UI_SEVERITIES);
    const result = new Set<string>(selected);

    if (baselineFilter.length === 0) {
      // Baseline meant "all severities"; preserve the non-UI severities it
      // implicitly covered (e.g. 'error') now that we must enumerate.
      for (const sev of BACKEND_NON_UI_SEVERITIES) {
        result.add(sev);
      }
    } else {
      for (const sev of baselineFilter) {
        if (!uiSet.has(sev.toLowerCase())) {
          result.add(sev);
        }
      }
    }

    return Array.from(result);
  }

  /**
   * Resolve the backend `digest` value for a save.
   *
   * The UI only models the `weeklyDigest` toggle and a three-value `frequency`
   * control, so it cannot represent the backend `never` digest (see
   * VALID_DIGEST_FREQUENCIES in notification_preferences.rs). To avoid silently
   * coercing `never`→`instant` on an unrelated save, the baseline digest is
   * preserved whenever the operator left the digest-relevant controls exactly
   * as loaded. Only when those controls actually changed is the digest derived
   * from the UI state.
   */
  private resolveDigest(ui: EmailNotificationSettings, baselineDigest: string): string {
    const baselineWeekly = baselineDigest === 'weekly';
    const baselineFrequency = this.digestToFrequency(baselineDigest);
    const digestControlsUnchanged =
      ui.weeklyDigest === baselineWeekly && ui.frequency === baselineFrequency;

    // Nothing the UI's digest controls govern changed — keep the backend value
    // verbatim (preserves 'never', and 'daily'/'weekly'↔'hourly' rounding too).
    if (digestControlsUnchanged) {
      return baselineDigest;
    }

    return this.frequencyToDigest(ui);
  }

  private frequencyToDigest(ui: EmailNotificationSettings): string {
    if (ui.weeklyDigest) {
      return 'weekly';
    }
    switch (ui.frequency) {
      case 'daily':
      case 'hourly':
        // No 'hourly' backend digest value; nearest cadence is 'daily'.
        return 'daily';
      case 'immediate':
      default:
        return 'instant';
    }
  }

  // ==========================================================================
  // Logging / error handling
  // ==========================================================================

  /**
   * Log debug messages
   */
  private logDebug(operation: string, message: string): void {
    this.logger.debug(message, { module: 'NotificationPreferencesService', operation });
  }

  /**
   * Generic error handler for HTTP operations
   */
  private handleError(operation: string) {
    return (error: HttpErrorResponse): Observable<never> => {
      if (!(error instanceof HttpErrorResponse)) {return throwError(() => error);}
      let errorMessage = 'An unknown error occurred';

      if (error.error instanceof ErrorEvent) {
        errorMessage = `Network error: ${error.error.message}`;
      } else {
        switch (error.status) {
          case 0:
            errorMessage = 'Unable to connect to server. Please check your internet connection.';
            break;
          case 400:
            errorMessage = error.error?.message || 'Invalid preferences data';
            break;
          case 401:
            errorMessage = 'Authentication required. Please log in again.';
            break;
          case 403:
            errorMessage = 'You do not have permission to perform this action';
            break;
          case 404:
            errorMessage = 'User preferences not found';
            break;
          case 500:
          case 502:
          case 503:
          case 504:
            errorMessage = 'Server error. Please try again later.';
            break;
          default:
            errorMessage = error.error?.message || `Server returned error ${error.status}`;
        }
      }

      this.logger.warn(`${operation} failed`, { module: 'NotificationPreferencesService', operation, errorMessage });
      return throwError(() => new Error(errorMessage));
    };
  }
}
